import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { DocumentDao } from '@app/dao/document/document.dao';

import { UserDao } from '@app/dao/user/user.dao';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import {
  Observable,
  Subject,
  catchError,
  finalize,
  map,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs';

import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { API_ROUTE_DOCUMENT } from '@app/forms/subscription/api/api-router';
import { PledgeRead } from '@app/forms/subscription/definition/interface/Pledge';
import {
  applyPickedGedDocument,
  getDocumentRoute,
  resetDocumentForm,
} from '../utile';
import { EaDocumentFormBuilder } from './ea-subscription-form.builder';
const DOCUMENT_TYPE = 'BENEFICIAIRES_EFFECTIFS';
@Component({
  selector: 'attestation-beneficiaires-effectifs-form',
  templateUrl: './attestation-beneficiaires-effectifs-form.component.html',
  styleUrls: ['./attestation-beneficiaires-effectifs-form.component.scss'],
  standalone: false,
})
export class AttestationBeneficiairesEffectifsFormComponent
  extends EaDocumentFormBuilder
  implements OnInit
{
  files: File[] = [];
  file: any;
  error: string;
  user: UserAuthentified;
  private alive = true;
  documents: any;
  docLoading: boolean = false;
  doneSubject: Subject<void> = new Subject<void>();
  obser$: any;
  formData: any;
  documentId: any;
  pledge: PledgeRead;
  gedDocuments = [DOCUMENT_TYPE];

  constructor(
    protected formConstructor: PrFormConstructor,
    private documentDao: DocumentDao,
    protected subscriptionFacade: SubscriptionFacadeService,
    private userDao: UserDao,
    private cdr: ChangeDetectorRef,
    private httpApiService: HttpApiService,
  ) {
    super(formConstructor, subscriptionFacade);
  }

  ngOnInit() {
    this.loading = true;
    this.initializePledge();

    this.initializeForm();
    this.fetchDocumentData();
  }
  initializePledge() {
    this.pledge = this.subscriptionFacade.getPledge();
  }
  initializeForm() {
    this.initForm({ document: DOCUMENT_TYPE });
  }
  fetchDocumentData() {
    this.obser$ = this.fetchUser().pipe(
      switchMap(user => this.fetchDocument(user.accountId)),
      takeWhile(() => this.alive),
      map((resp: any) => (resp?.length ? resp[0] : {})),
      tap(resp => this.initializeFormWithData(resp)),
      take(1),
      catchError(err => {
        this.loading = false;
        return err;
      }),
      finalize(() => {
        this.loading = false;
        this.cdr.detectChanges();
      }),
    );
  }
  private fetchUser(): Observable<any> {
    return this.userDao.getAuthentified().pipe(
      tap(user => {
        this.user = user;
      }),
    );
  }
  private fetchDocument(accountId: string): Observable<any> {
    return this.documentDao.searchDocument(accountId, {
      documents: [DOCUMENT_TYPE],
    });
  }
  private initializeFormWithData(data: any): void {
    const formData = {
      ...data,
      document: DOCUMENT_TYPE,
      parameters: {
        ...this.pledge?.parameters,
      },
    };
    this.initForm(formData);
  }
  onLoadFiles(files: any) {
    this.files = files;
    if (this.files.length > 0) {
      this.error = null;
    }
  }

  get title() {
    return 'Ajoutez un nouveau document';
  }
  downloadDocument() {
    const doc = this.entity;
    this.docLoading = true;

    const route = getDocumentRoute(doc.id, this.user?.accountId);
    this.httpApiService
      .getWithOptions(route, {
        responseType: 'arraybuffer',
      })
      .pipe(
        take(1),
        catchError(err => {
          this.docLoading = false;
          this.notificationService.error(err.message);
          console.error(err);
          return err;
        }),
        finalize(() => {
          this.docLoading = false;
        }),
      )
      .subscribe(resp => {
        this.saveDocumentFile(resp, doc.label);
        this.docLoading = false;
        this.cdr.detectChanges();
      });
  }
  saveDocumentFile(data: ArrayBuffer, label: string) {
    const blob = new Blob([data], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${label}.pdf`;
    link.click();
  }
  deleteDocument(doc) {
    const route = API_ROUTE_DOCUMENT.replace(
      ':accountId',
      this.user?.accountId?.toString(),
    ).replace(':documentId', doc.id);
    this.httpApiService
      .delete(route)
      .pipe(take(1))
      .subscribe(() => {
        resetDocumentForm(this, DOCUMENT_TYPE);
        this.cdr.detectChanges();
      });
  }
  onSubmit() {
    this.prFormService.loading();
    if (!this.files?.length && !this.form.get('id')?.value) {
      this.error = 'Veuillez saisir au moins un fichier';
      return;
    }
    const formData = this.createFormData();
    this.formData = formData;
    this.saveFormData().subscribe(
      success => {
        this.prFormService.loaded();
        this.doneSubject.next();
      },
      errors => {
        console.error(errors);
        this.prFormService.loaded();
        this.notificationService.error('La sauvegarde a échoué');
      },
    );
  }
  private createFormData(): FormData {
    const formData = new FormData();
    for (const file of this.files) {
      formData.append('documentsFiles', file);
    }
    const obj = this.extractDocument().getRawValue();
    const payloadDocument = this.formatPayloadForSubmitDocument(obj);

    formData.append(
      'document',
      new Blob([JSON.stringify(payloadDocument)], {
        type: 'application/json',
      }),
    );

    return formData;
  }

  private formatPayloadForSubmitDocument(payload: any) {
    return {
      label: payload.label,
      dateCreated: payload.dateCreated,
      reviewed: payload.reviewed,
      isSigned: payload.isSigned,
      accountId: payload.accountId,
      category: payload.category,
      dateExpired: payload.dateExpired,
      document: payload.document,
      id: payload.id,
      ownerIds:
        payload.ownerIds && payload.ownerIds.length
          ? payload.ownerIds
          : payload.ownerIds
            ? [payload.ownerIds]
            : [],
    };
  }

  saveFormData() {
    if (!this.form.get('id').value) {
      return this.createDocument(this.formData);
    } else {
      return this.updateDocument();
    }
  }

  updateDocument() {
    return this.documentDao
      .putDocument(this.extractDocument().getRawValue(), this.user.accountId)
      .pipe(
        take(1),
        tap(resp => {
          this.documentId = resp.id;
          this.subscriptionFacade.submitPledgeWithoutForm(this);
        }),
      );
  }
  createDocument(formData) {
    return this.documentDao.saveDocument(formData, this.user.accountId).pipe(
      take(1),
      tap(resp => {
        this.documentId = resp.id;
        this.subscriptionFacade.submitPledgeWithoutForm(this);
      }),
    );
  }
  extract() {
    return {
      gedAttestationBeneficiairesEffectifsId: this.documentId,
    };
  }

  onGedDocumentSelected(doc: any) {
    applyPickedGedDocument(this, doc, DOCUMENT_TYPE);
    this.cdr.detectChanges();
  }

  withdraw() {
    resetDocumentForm(this, DOCUMENT_TYPE);
    this.cdr.detectChanges();
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
  printFormData(formData: FormData) {
    formData.forEach((value, key) => {
      console.log(key, value);
    });
  }
}
